import type { CurseValue, DirValue, ItemValue } from './constants';

/** One byte of intent per tick. Everything the sim needs from a controller. */
export interface Input {
  /** -1, 0 or 1. */
  mx: number;
  /** -1, 0 or 1. */
  my: number;
  /** Rising edge places a bomb (or punches / detonates, see below). */
  bomb: boolean;
  /**
   * Secondary action: detonates remote bombs, or punches the bomb the player
   * is facing when they hold the Power Glove.
   */
  action: boolean;
}

export const EMPTY_INPUT: Input = { mx: 0, my: 0, bomb: false, action: false };

/**
 * A creature. Not a player, which is the entire point.
 *
 * Every opponent this game has ever had was a `PlayerState` with `bot: true` --
 * same movement, same bombs, same drop table, wearing one of the eight bomber
 * meshes. That is the right opponent for a deathmatch and the wrong one for a
 * run: it made Expedition eight rounds of PvP with a save file, and the bots
 * detoured for the same power-ups the run is built on carrying.
 *
 * A creature cannot place a bomb, cannot pick anything up, and kills by being
 * touched. So a room stops being a fight you might lose to a better duellist
 * and becomes a question about geometry and timing, which is what the series
 * has always done in single player: contact damage, and the variety lives in
 * how the thing moves.
 */
export const EnemyKind = {
  /** Walks a straight line, turns at whatever stops it. Kill it in its lane. */
  DRIFTER: 0,
  /** Slow, walks toward you when close. Kill it by leading it onto a bomb. */
  STALKER: 1,
  /** Slow, walks through soft blocks. Cannot be walled off; meet it in the open. */
  PHASER: 2,
  /**
   * The last room. Large, slow, several hits, and it comes apart into drifters
   * rather than simply stopping.
   *
   * A rival bomber would have been the obvious ending and is the wrong one:
   * this mode exists because fighting copies of yourself was the problem, and
   * finishing a run against one would say the creatures were the warm-up. So
   * the ending is the same verb the run has been teaching, at a scale where
   * one bomb is not the answer -- and the split is what makes the last hit
   * land, because the room gets worse before it ends.
   */
  BROODER: 3,
} as const;
export type EnemyKindValue = (typeof EnemyKind)[keyof typeof EnemyKind];

export interface EnemyState {
  id: string;
  kind: EnemyKindValue;
  x: number;
  y: number;
  dir: DirValue;
  alive: boolean;
  /** Blasts it survives. 1 for the common species. */
  hp: number;
  /** The one a `hunt` room is about. Drawn larger and brighter. */
  hunted: boolean;
  /** Tiles per second. */
  speed: number;
  /**
   * Ticks before it may be hurt again.
   *
   * A blast sits on its tile for `BLAST_TICKS`, so without this a two-hit
   * creature dies to a single bomb and the hit points mean nothing.
   */
  hurtUntil: number;
}

export interface PlayerState {
  id: string;
  name: string;
  slot: number;
  team: number;
  bot: boolean;
  /** Bot difficulty 0-3; ignored for humans. */
  botLevel: number;

  x: number;
  y: number;
  dir: DirValue;
  /** Set while the player is physically moving, for animation. */
  moving: boolean;

  alive: boolean;
  lives: number;
  respawnAt: number;

  maxBombs: number;
  bombsActive: number;
  power: number;
  speedLevel: number;

  kick: boolean;
  punch: boolean;
  remote: boolean;
  pierce: boolean;

  /**
   * Carried motion: a vent's launch, or ice refusing to let you stop.
   *
   * Ticks remaining and a velocity in tiles/second. While it runs the player's
   * own input is ignored for a launch and merely blended for ice, which is the
   * difference between being thrown and sliding.
   *
   * Snapshots include carry velocity and expiry so a client joining mid-launch
   * can predict the remaining motion with the shared movement functions.
   */
  carryUntil: number;
  carryVx: number;
  carryVy: number;
  /** True while the carry is a launch rather than a slide. */
  launched: boolean;

  invulnUntil: number;
  shieldUntil: number;
  ghostUntil: number;
  curse: CurseValue;
  curseUntil: number;
  /**
   * An Overload waiting to be spent: the next bomb detonates at full range and
   * pierces. Granted on respawn to a player who is materially behind, and
   * spent by placing a bomb.
   */
  charged: boolean;
  /**
   * Hits remaining before this player dies.
   *
   * 1 in the game as shipped, where any blast is fatal. Above 1 when the match
   * asks for it, and the whole point of asking: an elimination stops being a
   * single instant a weaker player can lose to and becomes an exchange they
   * can survive.
   */
  hp: number;
  /**
   * How far behind the field this player is, 0 to 1, recomputed as they play.
   *
   * Drives resistance and lethality. Stored rather than derived on demand so
   * the renderer, the HUD and the bots all read one number that was computed
   * once, and so it can be put on the wire -- an opponent who cannot see that
   * someone is resolved is being surprised rather than outplayed.
   */
  resolve: number;

  kills: number;
  deaths: number;
  score: number;
  /** Rising-edge latches so a held button does not repeat. */
  prevBomb: boolean;
  prevAction: boolean;
}

export interface BombState {
  id: number;
  ownerId: string;
  x: number;
  y: number;
  fuse: number;
  power: number;
  pierce: boolean;
  remote: boolean;
  /** Kick velocity in tiles/second, or null when at rest. */
  vx: number;
  vy: number;
  /** Punch arc: remaining ticks of flight, 0 when grounded. */
  flight: number;
  flightFrom: [number, number];
  flightTo: [number, number];
  /** Players currently allowed to overlap this bomb (they just placed it). */
  noCollide: string[];
}

export interface BlastState {
  id: number;
  ownerId: string;
  /** Flat cell indices covered by this blast. */
  cells: number[];
  /** Per-cell render hint: 0 centre, 1 arm, 2 tip; parallel to `cells`. */
  kinds: number[];
  /** Where the bomb stood, so damage can fall off along the ray. */
  ox: number;
  oy: number;
  power: number;
  /** Per-cell axis: 0 horizontal, 1 vertical; parallel to `cells`. */
  axes: number[];
  ticksLeft: number;
}

export type MatchPhase = 'lobby' | 'countdown' | 'playing' | 'sudden-death' | 'over';

/**
 * How a match is won.
 *
 * `lives` is elimination: you get `lives` of them, and the last bomber standing
 * takes it. The other two are deathmatch — death costs nothing but the respawn
 * timer, and the winner is whoever landed the most kills, either first to
 * `killTarget` or most when the clock runs out.
 */
export type WinCondition = 'lives' | 'kills' | 'timed';

export interface MatchSettings {
  mapId: string;
  mode: 'ffa' | 'teams' | 'survival';
  roundSeconds: number;
  lives: number;
  seed: number;
  suddenDeath: boolean;
  friendlyFire: boolean;
  itemDensity: number;
  winBy: WinCondition;
  /** Kills that win a `kills` match outright. Ignored by the other modes. */
  killTarget: number;
  /**
   * Bend the drop table toward whoever is behind. Absent means on.
   *
   * Off reproduces the flat table this game shipped with, which is what a
   * tournament wants and what an A/B of the mechanic needs.
   */
  catchup?: boolean;
  /**
   * Mid-match supply drops. Off unless asked for, while it is being judged.
   *
   * The half of the original argument the balance work never reached: Mario
   * Kart does not only help the player behind, it puts events on the track
   * that are worth going for. Everything shipped so far narrows the gap; this
   * is the first thing that gives anybody a reason to take a risk.
   */
  arenaEvents?: boolean;
  /**
   * A bomb already in the air can be punched again.
   *
   * The ceiling half of the argument the balance work never reached: every
   * mechanic so far narrows the gap between players, and none of them gives a
   * good one anything new to be good at. A bomb punched at you can be punched
   * back, which is a skill and a counter at the same time -- the answer to an
   * incoming bomb stops being "run".
   */
  deflect?: boolean;
  /**
   * Hits a player can take before dying. Absent or 1 is the original game.
   *
   * The measurement this exists for: whoever leads on kills at thirty seconds
   * wins 65% of a four-player match and up to 75% of an eight-player one,
   * against 25% and 12.5% chance. No redistribution of items moved that, in
   * five attempts, because the compounding unit is the elimination itself.
   * This is the only lever tried so far that changes what an elimination *is*
   * rather than what surrounds it.
   */
  hitPoints?: number;
  /**
   * Lose the special power-ups on death, keeping range and bomb count.
   *
   * This is the Bomberman series convention and this game quietly departed
   * from it: Saturn Bomberman drops all specials on a lost life, the NES game
   * and Super Bomberman keep only Fire Up and Bomb Up. Here death has always
   * cost a respawn timer and nothing else, which is what makes a material lead
   * permanent.
   *
   * The convention also happens to match what this project measured. Gear is
   * the second strongest predictor of the winner at 2.1-4.5x chance and speed
   * the third at 1.5-2.8x, while range and bomb count sit at 1.0-1.5x, which
   * is noise. The series rule takes the two that matter and leaves the two
   * that do not -- which is a better-aimed change than anything derived here
   * from first principles.
   */
  loseGearOnDeath?: boolean;
  /**
   * Scale resistance and lethality by how far behind a player is.
   *
   * Needs `hitPoints` above 1 to mean anything: in a one-hit game every blast
   * is fatal and there is no damage to scale. That pairing is the point --
   * health is what turns a match from about four decisive events into a
   * continuous exchange, and a continuous mechanic needs continuous events.
   */
  resolve?: boolean;
  /**
   * Which look the host asked the arena to wear, or absent for "let the seed
   * decide". Lobby intent, not match state: the room turns it into a seed at
   * kick-off and every client still derives the look from that seed, so
   * nothing downstream reads this field.
   */
  arenaLook?: number;
  /**
   * What ends the room, when a room is a creature room.
   *
   * Expedition's rooms have exactly one player in them, and last-one-standing
   * cannot decide a match with nobody to be last against -- `checkVictory`
   * bails out below two players so training does not declare a winner on tick
   * one. So a creature room needs its own condition, and this is where it
   * comes from. Absent means an ordinary match, decided the ordinary way.
   */
  roomGoal?: RoomGoalValue;
  /** What to put in the room. Read once, at construction. */
  enemies?: EnemySpawn[];
}

/**
 * What a room asks of you.
 *
 * One objective was the right place to start and the wrong place to stop:
 * eight rooms that all end when the last creature dies is one room eight
 * times, which is the failure the whole mode was shaped to avoid. These are
 * chosen so the *verb* differs, not just the number -- and creatures are what
 * makes that possible, because you can run past a creature. You could never
 * run past a bot holding a bomb, so with bombers there was only ever one
 * objective worth having.
 */
export const RoomGoal = {
  /** Kill every creature. The baseline, and always room one. */
  CLEAR: 'clear',
  /** Reach the exit. The creatures are terrain now, not a checklist. */
  EXIT: 'exit',
  /** Stay alive until the clock runs out. Killing is optional and often wrong. */
  SURVIVE: 'survive',
  /** One of them is marked. Kill that one and leave; the rest do not matter. */
  HUNT: 'hunt',
} as const;
export type RoomGoalValue = (typeof RoomGoal)[keyof typeof RoomGoal];

/** One creature to place, as a room asks for it. */
export interface EnemySpawn {
  kind: EnemyKindValue;
  count: number;
}

export interface GameState {
  tick: number;
  phase: MatchPhase;
  width: number;
  height: number;
  /** Flat `width * height` tile array. */
  grid: Uint8Array;
  /** Flat `width * height` item overlay. */
  items: Uint8Array;
  /**
   * Flat `width * height` hazard overlay. See `shared/hazards.ts`.
   *
   * Fixed for the life of a match -- a wire is always a wire -- so unlike
   * `grid` and `items` it never changes and never needs to reach the client as
   * a delta. Whether it is *live* is a function of the tick, which both sides
   * compute for themselves.
   */
  hazards: Uint8Array;
  players: PlayerState[];
  /**
   * Creatures. Empty in every mode but Expedition, and never sent over the
   * wire: a run is local, so the snapshot format does not have to know.
   */
  enemies: EnemyState[];
  bombs: BombState[];
  blasts: BlastState[];
  settings: MatchSettings;
  /** Ticks until the round timer expires. */
  timeLeft: number;
  /**
   * Cell index of the exit, or -1 where the room has none.
   *
   * On the state rather than derived, because the renderer draws it, the HUD
   * points at it and `checkVictory` stands on it -- three readers that must
   * agree on one tile.
   */
  exitAt: number;
  /** Sudden-death spiral cursor. */
  sdIndex: number;
  sdTimer: number;
  /** Winning slot(s) once `phase === 'over'`. */
  winners: string[];
  /** Survival mode: current wave number. */
  wave: number;
  nextBombId: number;
  nextBlastId: number;
}

/** Side-effects emitted by a tick, consumed by audio + VFX. Not authoritative. */
export type GameEvent =
  | { t: 'bomb'; x: number; y: number; owner: string }
  | { t: 'explode'; x: number; y: number; power: number; cells: number[]; kinds: number[]; axes: number[] }
  | { t: 'break'; x: number; y: number }
  | { t: 'pickup'; x: number; y: number; item: ItemValue; player: string }
  | { t: 'death'; x: number; y: number; player: string; killer: string | null }
  | { t: 'kick'; x: number; y: number }
  | { t: 'punch'; x: number; y: number }
  | { t: 'curse'; player: string; curse: CurseValue }
  | { t: 'charge'; player: string }
  | { t: 'hurt'; player: string; hp: number; x: number; y: number; by: string | null }
  | { t: 'overload'; player: string; x: number; y: number }
  | { t: 'supply'; cells: number[] }
  | { t: 'supply-soon' }
  | { t: 'sudden-death' }
  | { t: 'crush'; x: number; y: number }
  | { t: 'phase'; phase: MatchPhase }
  | { t: 'wave'; wave: number }
  | { t: 'enemy-hurt'; id: string; x: number; y: number; kind: EnemyKindValue }
  | { t: 'enemy-death'; id: string; x: number; y: number; kind: EnemyKindValue }
  | { t: 'room-clear' }
  | { t: 'zap'; x: number; y: number; player: string }
  | { t: 'launch'; x: number; y: number; player: string };
